import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Search, ArrowLeft, Link2, X } from "lucide-react";

const TYPE_BADGE = { hotel: "bg-indigo-50 text-indigo-700", transfer: "bg-cyan-50 text-cyan-700", actividad: "bg-amber-50 text-amber-700" };

/** Diálogo para asociar manualmente una respuesta "sin asignar" a un viaje y sus reservas.
 *  Paso 1: buscar viaje por cliente/referencia con autocompletado (debounce 250ms)
 *  Paso 2: elegir reservas (todas preseleccionadas por defecto + buscador interno + toggle todas/ninguna). */
export default function AssociateReplyDialog({ reply, open, onOpenChange, onUpdated }) {
  const [q, setQ] = useState("");
  const [trips, setTrips] = useState(null); // null = aún no se buscó
  const [searching, setSearching] = useState(false);
  const [trip, setTrip] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState({});
  const [bookingFilter, setBookingFilter] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setQ(""); setTrips(null); setTrip(null);
      setBookings([]); setSelected({}); setBookingFilter("");
    }
  }, [open]);

  // Typeahead: busca cuando q cambia (con debounce). Vacío => limpia resultados.
  useEffect(() => {
    if (trip) return; // ya elegimos viaje, no busques
    const term = q.trim();
    if (term.length < 2) { setTrips(null); return; }
    let cancel = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/trips?mine=false&q=${encodeURIComponent(term)}`);
        if (!cancel) setTrips(data.slice(0, 25));
      } catch {
        if (!cancel) setTrips([]);
      } finally {
        if (!cancel) setSearching(false);
      }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, trip]);

  const pickTrip = async (t) => {
    setTrip(t);
    try {
      const { data } = await api.get(`/trips/${t.id}/bookings`);
      setBookings(data);
      // Todas DESELECCIONADAS por defecto — el usuario elige a cuál(es) asociar
      const pre = {};
      data.forEach((b) => { pre[b.id] = false; });
      setSelected(pre);
    } catch {
      toast.error("Error cargando las reservas del viaje");
    }
  };

  // Filtro local del listado de reservas (typeahead sobre service_type, descripción, fecha, hora)
  const filteredBookings = useMemo(() => {
    const term = bookingFilter.trim().toLowerCase();
    if (!term) return bookings;
    return bookings.filter((b) => {
      const hay = `${b.service_type || ""} ${b.description || ""} ${b.date || ""} ${b.time || ""} ${b.notes || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [bookings, bookingFilter]);

  const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const visibleIds = filteredBookings.map((b) => b.id);
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
  const toggleAllVisible = () => {
    const next = { ...selected };
    if (allVisibleChecked) visibleIds.forEach((id) => { next[id] = false; });
    else visibleIds.forEach((id) => { next[id] = true; });
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/inbox/replies/${reply.id}/associate`, {
        trip_id: trip.id,
        booking_ids: ids,
        reanalyze: true,
      });
      toast.success(data.reanalyzed
        ? `Respuesta asociada a ${ids.length} reserva${ids.length > 1 ? "s" : ""} y re-analizada por la IA`
        : "Respuesta asociada (la IA no pudo re-analizar — se conserva el análisis anterior)");
      onUpdated?.(data.reply);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo asociar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-xl" data-testid="associate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 size={16} /> Asociar respuesta a un viaje</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-gray-500 -mt-1">
          De: <b>{reply.from_name || reply.from_email}</b> · {reply.subject}
        </div>

        {!trip ? (
          <div className="space-y-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                className="pl-9 pr-9"
                placeholder="Empieza a escribir cliente o referencia…"
                data-testid="associate-search-input"
              />
              {searching && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
              )}
              {!searching && q && (
                <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="associate-search-clear">
                  <X size={14} />
                </button>
              )}
            </div>
            {q.trim().length > 0 && q.trim().length < 2 && (
              <p className="text-[11px] text-gray-400 px-1">Escribe al menos 2 letras…</p>
            )}
            {trips !== null && (
              <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {trips.length === 0 && !searching && (
                  <p className="text-xs text-gray-400 p-3" data-testid="associate-no-results">
                    Sin resultados — prueba con otro nombre o referencia.
                  </p>
                )}
                {trips.map((t) => (
                  <button key={t.id} onClick={() => pickTrip(t)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50/60 transition-colors"
                    data-testid={`associate-trip-${t.id}`}>
                    <div className="text-sm font-medium">{t.client_name || "(sin cliente)"}</div>
                    <div className="text-[11px] text-gray-500">
                      {t.reference || "sin ref"} · {t.arrival_date || "?"} → {t.departure_date || "?"} {t.destination ? `· ${t.destination}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <button onClick={() => { setTrip(null); setBookings([]); setSelected({}); setBookingFilter(""); }}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" data-testid="associate-back">
              <ArrowLeft size={12} /> Cambiar de viaje
            </button>
            <div className="rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2">
              <div className="text-sm font-medium">{trip.client_name}</div>
              <div className="text-[11px] text-gray-500">{trip.reference || "sin ref"}</div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-gray-700">
                Reservas del viaje <span className="text-gray-400">({ids.length}/{bookings.length} seleccionadas)</span>
              </p>
              {visibleIds.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllVisible}
                  className="text-[11px] text-blue-600 hover:underline"
                  data-testid="associate-toggle-all"
                >
                  {allVisibleChecked ? "Deseleccionar todas" : "Seleccionar todas"}
                </button>
              )}
            </div>
            {bookings.length > 4 && (
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={bookingFilter}
                  onChange={(e) => setBookingFilter(e.target.value)}
                  placeholder="Filtrar reservas (tipo, descripción, fecha)…"
                  className="pl-7 h-8 text-xs"
                  data-testid="associate-booking-filter"
                />
              </div>
            )}
            <div className="max-h-60 overflow-y-auto space-y-1">
              {bookings.length === 0 && <p className="text-xs text-gray-400">Este viaje no tiene reservas.</p>}
              {bookings.length > 0 && filteredBookings.length === 0 && (
                <p className="text-xs text-gray-400">Ninguna reserva coincide con el filtro.</p>
              )}
              {filteredBookings.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer rounded-md px-2 py-1.5 hover:bg-gray-50 border border-transparent has-[:checked]:border-blue-200 has-[:checked]:bg-blue-50/40">
                  <Checkbox checked={!!selected[b.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [b.id]: !!v }))}
                    data-testid={`associate-booking-${b.id}`} />
                  <span className={`text-[10px] rounded px-1.5 py-0.5 ${TYPE_BADGE[b.service_type] || "bg-gray-100 text-gray-600"}`}>{b.service_type}</span>
                  <span className="text-gray-500 whitespace-nowrap">{b.date}</span>
                  <span className="truncate">{b.description}</span>
                  {b.price != null && <span className="ml-auto text-[10px] text-emerald-700 whitespace-nowrap">{b.price} €</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="items-center gap-2">
          {saving && <span className="text-[11px] text-blue-600 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Asociando y re-analizando con IA…</span>}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={save} disabled={!trip || ids.length === 0 || saving}
            className="bg-violet-600 hover:bg-violet-700" data-testid="associate-confirm">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />}
            Asociar {ids.length > 0 ? `${ids.length} reserva${ids.length > 1 ? "s" : ""}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
